/* ==========================================================================
   Collection "Load more" with a back button that works.

   - The first batch is server-rendered. "Vis flere" appends the next page via
     the Section Rendering API. Nothing loads on scroll.
   - Loading more updates ?page=N on the CURRENT history entry (replaceState,
     never pushState → Back leaves the collection instead of stepping through
     pages). The loaded range and the product the visitor was looking at live
     in history.state for this entry, mirrored in sessionStorage in case
     another script clobbers history.state. Returning to ?page=N restores
     pages 1..N and the clicked product.
   - Back with bfcache: the page is restored as-is (re-placed only if it drifted).
     Back without bfcache, reload, or the product page's breadcrumb (it leaves
     a short-lived "return intent" in sessionStorage, see
     assets/product-back-link.js): the server renders only ?page=N. If the
     clicked card is on that page it is put back in place immediately, and the
     missing pages are re-fetched in parallel and inserted above/below it
     while it is held at the same viewport offset.
   - A fresh navigation (menu link, typed URL) always starts at the top.
   See docs/load-more-plan.md.
   ========================================================================== */
(() => {
  if (customElements.get('collection-load-more')) return;

  const STATE_KEY = 'collectionLoadMore';
  const STORAGE_PREFIX = 'clm:';
  const MAX_RESTORE_PAGES = 20;     // never re-fetch more than this on restore
  const RESTORE_CONCURRENCY = 4;
  const STORAGE_TTL = 30 * 60 * 1000;
  const DEFERRED = 'load-more-deferred';

  const navEntry = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
  const navType = navEntry ? navEntry.type : 'navigate';
  const designMode = !!(window.Shopify && window.Shopify.designMode);

  let booted = false;
  let quietUntil = 0;               // ignore scroll-driven anchor updates until then
  let current = null;               // the live <collection-load-more> instance

  /* ---------- helpers ---------- */

  const listingKey = (href = location.href) => {
    const url = new URL(href);
    url.searchParams.delete('page');
    url.searchParams.delete('section_id');
    url.searchParams.sort();
    return url.pathname + (url.search || '');
  };

  const getGrid = () => document.getElementById('product-grid-ajax');

  const productCards = (root = getGrid()) =>
    root ? Array.from(root.querySelectorAll(':scope > .card--product[id^="product-"]')) : [];

  const visibleProductCards = () => productCards().filter((card) => !card.classList.contains(DEFERRED));

  const instant = (top) => {
    quietUntil = Date.now() + 400;
    window.scrollTo({ top, left: 0, behavior: 'instant' });
  };

  /**
   * The product page's breadcrumb navigates here with a fresh page load when
   * it cannot use history.back(). It leaves { href, ts } under RETURN_KEY so
   * this load counts as a return to the listing, not a fresh visit. One-shot.
   */
  const RETURN_KEY = 'clm:return';
  const returnIntent = (() => {
    try {
      const raw = sessionStorage.getItem(RETURN_KEY);
      if (!raw) return false;
      sessionStorage.removeItem(RETURN_KEY);
      const intent = JSON.parse(raw);
      return !!(intent && intent.href && Date.now() - intent.ts < 60 * 1000
        && listingKey(new URL(intent.href, location.origin).href) === listingKey());
    } catch (e) { return false; }
  })();
  const isReturn = navType === 'back_forward' || navType === 'reload' || returnIntent;

  const readStored = (key) => {
    const fromHistory = history.state && typeof history.state === 'object' ? history.state[STATE_KEY] : null;
    if (fromHistory && fromHistory.key === key) return fromHistory;
    // history.state can be wiped by other scripts (replaceState('')), and a
    // breadcrumb return is a new entry without it. The sessionStorage mirror
    // is only trusted when returning, never on a fresh visit.
    if (!isReturn) return null;
    try {
      const raw = sessionStorage.getItem(STORAGE_PREFIX + key);
      const parsed = raw ? JSON.parse(raw) : null;
      if (parsed && parsed.key === key && Date.now() - parsed.ts < STORAGE_TTL) return parsed;
    } catch (e) { /* storage blocked */ }
    return null;
  };

  const writeStored = (state) => {
    try {
      const base = history.state && typeof history.state === 'object' ? history.state : {};
      history.replaceState({ ...base, [STATE_KEY]: state }, '', location.href);
    } catch (e) { /* throttled or blocked — sessionStorage still has it */ }
    try {
      sessionStorage.setItem(STORAGE_PREFIX + state.key, JSON.stringify(state));
    } catch (e) { /* storage blocked */ }
  };

  /** First product card whose bottom edge is inside the viewport (binary search: cards are in visual order). */
  const firstVisibleCard = () => {
    const cards = productCards();
    let lo = 0, hi = cards.length - 1, found = null;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      if (cards[mid].getBoundingClientRect().bottom > 0) { found = cards[mid]; hi = mid - 1; }
      else lo = mid + 1;
    }
    return found;
  };

  /** First rendered product link in a card (desktop/mobile sliders hide one of their copies). */
  const focusableLink = (card) => card
    ? Array.from(card.querySelectorAll('a[href*="/products/"]')).find((a) => a.getClientRects().length) || null
    : null;

  /** Reflect the last loaded page in the URL without adding a history entry. */
  const setUrlPage = (page) => {
    const url = new URL(location.href);
    if (page > 1) url.searchParams.set('page', page); else url.searchParams.delete('page');
    if (url.href === location.href) return;
    try { history.replaceState(history.state, '', url.toString()); } catch (e) { /* throttled */ }
  };

  const anchorFor = (card) => card ? { id: card.id, offset: Math.round(card.getBoundingClientRect().top) } : null;

  /* ---------- component ---------- */

  class CollectionLoadMore extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      this.sectionId = this.dataset.sectionId;
      this.totalPages = parseInt(this.dataset.totalPages, 10) || 1;
      this.total = parseInt(this.dataset.total, 10) || 0;
      this.first = parseInt(this.dataset.currentPage, 10) || 1;
      this.last = this.first;
      this.loading = false;

      this.button = this.querySelector('[data-load-more-button]');
      this.statusEl = this.querySelector('[data-load-more-status]');
      this.fillEl = this.querySelector('[data-load-more-fill]');
      this.errorEl = this.querySelector('[data-load-more-error]');
      this.previous = document.querySelector('[data-load-previous]');

      if (this.button) this.upgradeLink(this.button, () => this.loadMore());
      if (this.previous) this.upgradeLink(this.previous, () => this.loadPrevious());
      this.observeForPrefetch();

      this.render();

      const isFirstBoot = !booted;
      booted = true;
      current = this;

      if (isFirstBoot) {
        installGlobalListeners();
        if (!designMode && isReturn) {
          this.restore();
          return;
        }
      } else if (this.previous && this.first === 1) {
        // Facets/sort replaced the grid with page 1 — the "previous" control is stale.
        this.previous.closest('.load-more-previous')?.remove();
        this.previous = null;
      }
      this.save(null);
    }

    disconnectedCallback() {
      if (current === this) current = null;
      if (this.prefetchObserver) this.prefetchObserver.disconnect();
    }

    /*
     * A page takes 1-1.6 s to fetch. Inserting it that long after the tap counts as an
     * unexpected layout shift (the browser only excuses shifts within 500 ms of input), so
     * each "Vis flere" click scored ~0.5 CLS. Fetch the next page while the button scrolls
     * into reach, so the click inserts at once.
     */
    observeForPrefetch() {
      if (!('IntersectionObserver' in window)) return;
      this.prefetchObserver = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.prefetch(this.last + 1);
      }, { rootMargin: '0px 0px 1500px 0px' });
      this.prefetchObserver.observe(this);
    }

    prefetch(page) {
      if (page > this.totalPages || (this.pending && this.pending.page === page)) return;
      const promise = this.fetchPage(page);
      promise.catch(() => { if (this.pending && this.pending.promise === promise) this.pending = null; });
      this.pending = { page, promise };
    }

    /** Parsed nodes can be inserted only once, so a prefetched page is handed out once. */
    takePage(page) {
      const pending = this.pending;
      this.pending = null;
      if (pending && pending.page === page) return pending.promise.catch(() => this.fetchPage(page));
      return this.fetchPage(page);
    }

    /** Real links stay crawlable / no-JS friendly; plain clicks are handled in place. */
    upgradeLink(link, handler) {
      link.setAttribute('role', 'button');
      link.addEventListener('click', (event) => {
        if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        handler();
      });
      link.addEventListener('keydown', (event) => {
        if (event.key === ' ' || event.key === 'Spacebar') { event.preventDefault(); link.click(); }
      });
    }

    /* ----- state ----- */

    state(anchor) {
      return {
        key: listingKey(),
        first: this.first,
        last: this.last,
        anchor: anchor || null,
        scrollY: Math.round(window.scrollY),
        docHeight: document.documentElement.scrollHeight,
        ts: Date.now(),
      };
    }

    save(anchor) {
      writeStored(this.state(anchor));
    }

    /* ----- fetching ----- */

    pageUrl(page, withSection = true) {
      const url = new URL(location.href);
      url.hash = '';
      url.searchParams.delete('page');
      url.searchParams.delete('section_id');
      if (withSection) url.searchParams.set('section_id', this.sectionId);
      url.searchParams.set('page', page);
      return url.toString();
    }

    async fetchPage(page) {
      const parse = (html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const grid = doc.getElementById('product-grid-ajax');
        return grid ? Array.from(grid.children) : null;
      };
      // Section Rendering API first; full page as fallback (e.g. a split-test
      // template whose section id the default template doesn't know).
      try {
        const res = await fetch(this.pageUrl(page, true), { credentials: 'same-origin' });
        if (res.ok) {
          const nodes = parse(await res.text());
          if (nodes) return nodes;
        }
      } catch (e) { /* fall through */ }
      const res = await fetch(this.pageUrl(page, false), { credentials: 'same-origin' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const nodes = parse(await res.text());
      if (!nodes) throw new Error('No product grid in response');
      return nodes;
    }

    /** Append (or prepend) nodes, skipping product cards already in the grid. Returns the first inserted product card. */
    insert(nodes, where = 'append') {
      const grid = getGrid();
      // Blocks pinned to position 0/1 render at the top of EVERY server page.
      // In one continuous grid they belong at the very top only, once.
      const isProduct = (node) => node.classList && node.classList.contains('card--product');
      const leadingBlocks = (list) => {
        const out = [];
        for (const node of list) { if (isProduct(node)) break; out.push(node); }
        return out;
      };
      if (where === 'prepend') {
        leadingBlocks(Array.from(grid.children)).forEach((node) => node.remove());
      } else {
        const top = new Set(leadingBlocks(nodes));
        nodes = nodes.filter((node) => !top.has(node));
      }
      const existing = new Set(productCards(grid).map((card) => card.id));
      const fresh = nodes.filter((node) => !(node.id && node.id.startsWith('product-') && existing.has(node.id)));
      const fragment = document.createDocumentFragment();
      fresh.forEach((node) => fragment.appendChild(document.adoptNode(node)));
      if (where === 'prepend') grid.prepend(fragment);
      else grid.append(fragment);
      return fresh.find(isProduct) || null;
    }

    /* ----- UI ----- */

    setBusy(link, busy) {
      if (!link) return;
      link.classList.toggle('is-loading', busy);
      link.setAttribute('aria-busy', busy ? 'true' : 'false');
      if (busy) link.setAttribute('aria-disabled', 'true'); else link.removeAttribute('aria-disabled');
    }

    showError(show) {
      if (!this.errorEl) return;
      this.errorEl.hidden = !show;
      this.errorEl.textContent = show ? this.dataset.textError : '';
    }

    /**
     * A batch rarely fills its last row (grid blocks add items), which leaves a
     * ragged edge right above the button. Hold that incomplete row back until
     * the next batch arrives — it then flows in first, seamlessly.
     */
    trimTail() {
      const grid = getGrid();
      if (!grid) return;
      grid.querySelectorAll(`:scope > .${DEFERRED}`).forEach((el) => el.classList.remove(DEFERRED));
      if (this.last >= this.totalPages) return;
      const items = Array.from(grid.children).filter((el) => el.getClientRects().length);
      const lastItem = items[items.length - 1];
      if (!lastItem) return;
      const lastRect = lastItem.getBoundingClientRect();
      if (grid.getBoundingClientRect().right - lastRect.right < 2) return;   // row is full
      const row = items.filter((el) => Math.abs(el.getBoundingClientRect().top - lastRect.top) < 2);
      if (row.length === items.length) return;
      row.forEach((el) => el.classList.add(DEFERRED));
    }

    render() {
      this.trimTail();
      const shown = visibleProductCards().length;
      const done = this.last >= this.totalPages;
      const text = done
        ? this.dataset.textAll.replace('[total]', this.total)
        : this.dataset.textShowing.replace('[shown]', shown).replace('[total]', this.total);
      if (this.statusEl && this.statusEl.textContent !== text) this.statusEl.textContent = text;
      if (this.fillEl) this.fillEl.style.width = `${this.total ? Math.min(100, (shown / this.total) * 100) : 100}%`;
      if (this.button) {
        if (done) {
          this.button.remove();
          this.button = null;
          this.toggleAttribute('data-complete', true);
        } else {
          this.button.href = this.pageUrl(this.last + 1, false);
        }
      }
      if (this.previous) {
        if (this.first <= 1) {
          this.previous.closest('.load-more-previous')?.remove();
          this.previous = null;
        } else {
          this.previous.href = this.pageUrl(this.first - 1, false);
        }
      }
    }

    track(page) {
      if (!Array.isArray(window.dataLayer)) return;
      window.dataLayer.push({ event: 'collection_load_more', load_more_page: page, products_shown: visibleProductCards().length });
    }

    /* ----- actions ----- */

    async loadMore() {
      if (this.loading || this.last >= this.totalPages) return;
      this.loading = true;
      this.setBusy(this.button, true);
      this.showError(false);
      const page = this.last + 1;
      try {
        const nodes = await this.takePage(page);
        // Do not let the (focused) button become the scroll anchor.
        if (document.activeElement === this.button) this.button.blur();
        const held = getGrid().querySelector(`:scope > .card--product.${DEFERRED}`);
        const inserted = this.insert(nodes);
        const firstNew = held || inserted;
        this.last = page;
        setUrlPage(page);
        this.render();
        this.save(anchorFor(firstVisibleCard()));
        this.track(page);
        const link = focusableLink(firstNew);
        if (link) link.focus({ preventScroll: true });
        // A short batch can leave the button inside the prefetch zone, where the observer will not fire again.
        if (this.getBoundingClientRect().top < window.innerHeight + 1500) this.prefetch(this.last + 1);
      } catch (error) {
        console.error('[load-more]', error);
        this.showError(true);
      } finally {
        this.loading = false;
        this.setBusy(this.button, false);
      }
    }

    async loadPrevious() {
      if (this.loading || this.first <= 1) return;
      this.loading = true;
      this.setBusy(this.previous, true);
      const page = this.first - 1;
      try {
        const nodes = await this.fetchPage(page);
        // Keep what the visitor is looking at in place while content is added above.
        const keep = firstVisibleCard();
        const before = keep ? keep.getBoundingClientRect().top : 0;
        const firstNew = this.insert(nodes, 'prepend');
        if (keep) {
          // Re-check after layout settles: leaving scrollY 0 can collapse sticky headers above.
          const hold = () => instant(window.scrollY + keep.getBoundingClientRect().top - before);
          hold();
          requestAnimationFrame(() => requestAnimationFrame(hold));
        }
        this.first = page;
        this.render();
        this.save(anchorFor(firstVisibleCard()));
        this.track(page);
        const link = focusableLink(firstNew);
        if (link && this.first <= 1) link.focus({ preventScroll: true });
      } catch (error) {
        console.error('[load-more]', error);
        this.showError(true);
      } finally {
        this.loading = false;
        this.setBusy(this.previous, false);
      }
    }

    scrollToAnchor(stored) {
      const el = stored.anchor && document.getElementById(stored.anchor.id);
      if (el) instant(window.scrollY + el.getBoundingClientRect().top - stored.anchor.offset);
      else if (typeof stored.scrollY === 'number') instant(stored.scrollY);
    }

    holdAnchor(stored, userActed, busy = () => false) {
      const el = stored.anchor && document.getElementById(stored.anchor.id);
      if (!el) return Promise.resolve();
      const started = performance.now();
      let until = started + 1500;
      if (document.readyState !== 'complete') {
        // Window load re-measures the sticky filter bar; keep holding past it.
        until = started + 6000;
        window.addEventListener('load', () => { until = Math.min(until, performance.now() + 800); }, { once: true });
      }
      return new Promise((resolve) => {
        const tick = () => {
          if (busy()) until = Math.max(until, performance.now() + 1500);   // pages still being inserted
          if (userActed() || performance.now() > until || !el.isConnected) { resolve(); return; }
          const drift = el.getBoundingClientRect().top - stored.anchor.offset;
          if (Math.abs(drift) > 1) instant(window.scrollY + drift);
          requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      });
    }

    /**
     * Back (without bfcache), reload or a breadcrumb return: rebuild the loaded
     * range and put the visitor back on their card.
     *
     * The server renders only ?page=N. When the clicked card is on that page
     * (the usual case: the URL reflects the last loaded page) it is placed
     * BEFORE anything is fetched, and pages above it are inserted while it is
     * held at the same viewport offset, so the visitor never sees the top of
     * the listing while pages 1..N-1 load.
     */
    async restore() {
      const stored = readStored(listingKey());
      if (!stored) { this.save(null); return; }

      const anchorOf = () => {
        const el = stored.anchor && document.getElementById(stored.anchor.id);
        return el && el.getClientRects().length ? el : null;
      };
      let userActed = false;
      const markUser = () => { userActed = true; };
      const userEvents = ['wheel', 'touchstart', 'pointerdown', 'keydown'];
      userEvents.forEach((type) => window.addEventListener(type, markUser, { passive: true, once: true }));
      // We compensate for content inserted above ourselves; native scroll
      // anchoring (Chrome/Firefox, not Safari) would move the page a second time.
      const root = document.documentElement;
      const overflowAnchor = root.style.overflowAnchor;
      root.style.overflowAnchor = 'none';

      const above = [];
      for (let p = Math.max(stored.first, this.first - MAX_RESTORE_PAGES); p < this.first; p++) above.push(p);
      const target = Math.min(stored.last, this.totalPages, (above.length ? above[0] : this.first) + MAX_RESTORE_PAGES);
      const below = [];
      for (let p = this.last + 1; p <= target; p++) below.push(p);
      const grid = getGrid();

      let held = null;
      if (anchorOf()) {
        // The clicked card is in the server-rendered batch: put it back now,
        // and keep it there while the other pages load and are inserted.
        this.scrollToAnchor(stored);
        this.loading = !!(above.length || below.length);
        held = this.holdAnchor(stored, () => userActed, () => this.loading);
      } else if (below.length && !above.length) {
        // The card is on a page still to come. Reserve the height the grid had,
        // so we can sit at the old position while the missing pages arrive.
        const missing = Math.max(0, (stored.docHeight || 0) - root.scrollHeight);
        if (grid && missing) grid.style.minHeight = `${grid.offsetHeight + missing}px`;
        if (typeof stored.scrollY === 'number') instant(stored.scrollY);
      } else if (!above.length) {
        this.scrollToAnchor(stored);        // card gone (e.g. unpublished): old scroll position
      }

      if (above.length || below.length) {
        this.loading = true;
        this.setBusy(this.button, true);
        this.setBusy(this.previous, true);
        if (grid) grid.setAttribute('aria-busy', 'true');
        const pages = [...above, ...below];
        const results = new Array(pages.length);
        let cursor = 0;
        const worker = async () => {
          while (cursor < pages.length) {
            const i = cursor++;
            try { results[i] = await this.fetchPage(pages[i]); } catch (e) { results[i] = null; }
          }
        };
        await Promise.all(Array.from({ length: Math.min(RESTORE_CONCURRENCY, pages.length) }, worker));

        // Pages above: nearest first, so the range stays contiguous. Whatever
        // is on screen (the card, or what the visitor scrolled to) stays put.
        const keep = (!userActed && anchorOf()) || firstVisibleCard();
        const before = keep ? keep.getBoundingClientRect().top : 0;
        for (let i = above.length - 1; i >= 0; i--) {
          if (!results[i]) break;
          this.insert(results[i], 'prepend');
          this.first = above[i];
        }
        for (let i = 0; i < below.length; i++) {
          if (!results[above.length + i]) break;
          this.insert(results[above.length + i]);
          this.last = below[i];
        }
        if (grid) { grid.style.minHeight = ''; grid.removeAttribute('aria-busy'); }
        this.loading = false;
        this.setBusy(this.button, false);
        this.setBusy(this.previous, false);
        this.render();                      // may remove the "previous" control above the grid
        setUrlPage(this.last);
        if (!userActed) this.scrollToAnchor(stored);
        else if (keep && keep.isConnected) instant(window.scrollY + keep.getBoundingClientRect().top - before);
      }
      // Late layout (fonts, images, the sticky filter bar, card sliders, app
      // widgets, window load) can still move the card after we placed it. Hold
      // it in place for a short while — until the visitor touches, scrolls or types.
      await (held || this.holdAnchor(stored, () => userActed));
      root.style.overflowAnchor = overflowAnchor;
      userEvents.forEach((type) => window.removeEventListener(type, markUser));
      // Keep the restored anchor so a second back/reload lands in the same place.
      this.save(stored.anchor);
    }
  }

  /* ---------- page-level listeners (installed once) ---------- */

  function installGlobalListeners() {
    let timer = null;
    // Remember the card a visitor opens — capture phase, before navigation starts.
    document.addEventListener('click', (event) => {
      if (!current || current.loading) return;
      const link = event.target.closest && event.target.closest('a[href]');
      if (!link || !link.href.includes('/products/')) return;
      const card = link.closest('#product-grid-ajax > [id^="product-"]');
      if (!card) return;
      // The clicked card is THE anchor; don't let a pending scroll save replace it.
      clearTimeout(timer);
      quietUntil = Date.now() + 2000;
      current.save(anchorFor(card));
    }, true);

    // Back from bfcache: the page comes back as it was. Only re-place the
    // clicked card if something (a late layout shift) moved it noticeably.
    window.addEventListener('pageshow', (event) => {
      if (!event.persisted) return;
      try { sessionStorage.removeItem(RETURN_KEY); } catch (e) { /* storage blocked */ }
      const stored = history.state && typeof history.state === 'object' ? history.state[STATE_KEY] : null;
      if (!current || current.loading || !stored || stored.key !== listingKey() || !stored.anchor) return;
      const el = document.getElementById(stored.anchor.id);
      if (!el || !el.getClientRects().length) return;
      const drift = el.getBoundingClientRect().top - stored.anchor.offset;
      if (Math.abs(drift) > 40) instant(window.scrollY + drift);
    });

    // Column count changes (rotation, resize, grid/single view toggle) → re-trim.
    let columns = null;
    const retrim = () => {
      const grid = getGrid();
      if (!current || !grid || current.loading) return;
      const now = getComputedStyle(grid).gridTemplateColumns.split(' ').length;
      if (now === columns) return;
      columns = now;
      current.render();
    };
    let resizeTimer = null;
    window.addEventListener('resize', () => { clearTimeout(resizeTimer); resizeTimer = setTimeout(retrim, 150); });
    document.addEventListener('click', (event) => {
      if (event.target.closest && event.target.closest('.collection-grid-btn')) setTimeout(retrim, 60);
    });

    // Keep the anchor current while browsing (for reload, or leaving via the header).
    window.addEventListener('scroll', () => {
      if (!current || current.loading || Date.now() < quietUntil) return;
      clearTimeout(timer);
      timer = setTimeout(() => {
        if (current && !current.loading && Date.now() >= quietUntil) current.save(anchorFor(firstVisibleCard()));
      }, 250);
    }, { passive: true });
  }

  customElements.define('collection-load-more', CollectionLoadMore);
})();
